import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import {
  ClipboardCheck, CheckCircle2, ArrowRight, Shield, Clock,
  Wrench, Eye, Droplets, Settings, Phone, MapPin
} from 'lucide-react';

const HERO_IMG = 'https://images.unsplash.com/photo-1628744876657-abd5086695dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob21lJTIwd2luZG93JTIwZG9vciUyMGludGVyaW9yJTIwYnJpZ2h0fGVufDB8fHx8MTc5MDkyNzY1MHww&ixlib=rb-4.1.0&q=85';

const CHECK_STEPS = [
  { icon: Eye, title: 'Ispezione Completa', desc: 'Controlliamo ogni serramento: finestre, porte-finestre, persiane, tapparelle e porte blindate.' },
  { icon: Settings, title: 'Regolazione Meccanismi', desc: 'Verifichiamo e regoliamo cerniere, maniglie, chiusure multipunto e meccanismi di apertura/chiusura.' },
  { icon: Droplets, title: 'Lubrificazione Professionale', desc: 'Oliamo tutti i punti di movimento con prodotti specifici per garantire scorrevolezza e durata.' },
  { icon: ClipboardCheck, title: 'Report Dettagliato', desc: 'Registriamo lo stato di ogni serramento e ti consigliamo eventuali interventi futuri.' },
];

const BENEFITS = [
  'Durata dei serramenti prolungata nel tempo',
  'Risparmio energetico grazie a chiusure ermetiche',
  'Niente più cigolii, blocchi o difficoltà di apertura',
  'Sicurezza domestica sempre al massimo',
  'Valore della casa mantenuto e protetto',
];

export default function CheckInfo() {
  const navigate = useNavigate();

  useEffect(() => {
    const fonte = new URLSearchParams(window.location.search).get('fonte');
    if (fonte) localStorage.setItem('regola_fonte', fonte);
  }, []);

  const handlePrenota = () => {
    navigate('/check-generale');
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/logo-regola.png" alt="REGOLA" className="h-8" />
          </div>
          <Button onClick={handlePrenota} className="bg-blue-500 hover:bg-blue-600 text-white rounded-full text-sm font-semibold gap-2" data-testid="header-cta">
            Prenota il Check <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-1.5 rounded-full text-xs font-bold mb-6">
                <ClipboardCheck className="w-3.5 h-3.5" />
                IL NOSTRO SERVIZIO PRINCIPALE
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight mb-5" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Check Generale<br />
                <span className="text-blue-500">Serramenti</span>
              </h1>
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-8 max-w-lg">
                Controlliamo, registriamo, regoliamo e lubrifichiamo <strong>tutti i tuoi serramenti</strong> in un unico intervento professionale. Previeni i problemi prima che diventino costosi.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={handlePrenota} size="lg" className="bg-blue-500 hover:bg-blue-600 text-white rounded-full text-base font-semibold gap-2 px-8" data-testid="hero-cta">
                  Prenota Ora <ArrowRight className="w-5 h-5" />
                </Button>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Clock className="w-4 h-4" />
                  <span>Durata circa 1h 30min</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img src={HERO_IMG} alt="Serramenti moderni" className="w-full h-[340px] object-cover" />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-900">40+ anni di esperienza</p>
                  <p className="text-[11px] text-slate-500">Dal 1984 al tuo servizio</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chi Siamo */}
      <section className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-lg font-bold text-slate-900 mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Chi Siamo
            </h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              <strong>REGOLA</strong> nasce dall'esperienza di <strong>Rivas</strong>, azienda fondata nel 1984 specializzata nella manutenzione di serramenti in alluminio, PVC e legno.
              Con oltre 40 anni di lavoro sul campo, abbiamo creato un servizio dedicato esclusivamente alla <strong>cura e manutenzione</strong> dei tuoi infissi.
              Non vendiamo finestre: le facciamo funzionare al meglio, per sempre.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
            {[
              { n: '40+', l: 'Anni di esperienza' },
              { n: '4', l: 'Province servite' },
              { n: '1000+', l: 'Serramenti controllati' },
              { n: '100%', l: 'Clienti soddisfatti' },
            ].map((s, i) => (
              <div key={i} className="text-center p-4">
                <p className="text-2xl font-extrabold text-blue-500" style={{ fontFamily: "'Manrope', sans-serif" }}>{s.n}</p>
                <p className="text-xs text-slate-500 mt-1">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cosa Comprende */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-lg font-bold text-slate-900 mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Cosa Comprende il Check
            </h2>
            <p className="text-sm text-slate-500 max-w-lg mx-auto">
              Un intervento completo in 4 fasi per garantire il perfetto funzionamento di ogni serramento della tua casa.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {CHECK_STEPS.map((s, i) => (
              <Card key={i} className="border-0 shadow-sm rounded-xl hover:shadow-md transition-all hover:-translate-y-0.5" data-testid={`check-step-${i}`}>
                <CardContent className="p-6">
                  <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center mb-4">
                    <s.icon className="w-5 h-5 text-blue-500" />
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold text-blue-500">FASE {i + 1}</span>
                  </div>
                  <h3 className="text-sm font-bold text-slate-900 mb-2">{s.title}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">{s.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefici */}
      <section className="bg-slate-900 py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-lg font-bold text-white mb-6" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Perché Fare il Check?
              </h2>
              <ul className="space-y-4">
                {BENEFITS.map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-300">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-slate-800 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <Wrench className="w-6 h-6 text-blue-400" />
                <h3 className="text-base font-bold text-white">Zone Servite</h3>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {['Brescia', 'Verona', 'Mantova', 'Cremona'].map((p) => (
                  <div key={p} className="flex items-center gap-2 bg-slate-700 rounded-lg px-4 py-3">
                    <MapPin className="w-4 h-4 text-blue-400" />
                    <span className="text-sm text-white font-medium">{p}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-4">
                Il costo dell'uscita e il prezzo del check variano in base alla distanza e al numero di serramenti.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Finale */}
      <section className="py-16">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-lg font-bold text-slate-900 mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Pronto a prenderti cura dei tuoi serramenti?
          </h2>
          <p className="text-sm text-slate-500 mb-8">
            Prenota il tuo Check Generale in pochi passi. Scegli data e orario, e il nostro tecnico verrà direttamente a casa tua.
          </p>
          <Button onClick={handlePrenota} size="lg" className="bg-blue-500 hover:bg-blue-600 text-white rounded-full text-base font-semibold gap-2 px-10" data-testid="final-cta">
            Prenota il Check Generale <ArrowRight className="w-5 h-5" />
          </Button>
          <p className="text-xs text-slate-400 mt-4 flex items-center justify-center gap-2">
            <Phone className="w-3 h-3" />
            Oppure chiamaci per info
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-100 py-6">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-xs text-slate-400">REGOLA Manutenzione Serramenti — Dal 1984 al tuo servizio</p>
        </div>
      </footer>
    </div>
  );
}
